/**
 * Ported VERBATIM from `apr_template/src/components/ScoringDetailsPanel.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../compat/api';
import './ScoringDetailsModal.css';

// ─── Types ──────────────────────────────────────────────────────────────────
interface DimensionScore {
  dimension_id: string;
  dimension_name: string;
  dimension_score: number;
  component_scores: ComponentScore[];
}
interface ComponentScore {
  component_id: string;
  component_name: string;
  component_score: number;
  component_weight?: number;
  total_questions?: number;
  answered_questions?: number;
  completion_percentage?: number;
  question_scores?: QuestionScore[];
}
interface QuestionScore {
  question_id: string;
  question_text?: string;
  score: number;
  weight: number;
  weighted_score?: number;
}
interface ScoringData {
  application_id: string;
  application_name: string;
  application_id_code?: string;
  overall_score: number;
  recommendation: string;
  recommendation_color?: string;
  assessment_status?: string;
  dimension_scores: DimensionScore[];
}
interface ScoringThreshold {
  id: string;
  name: string;
  minScore: number;
  maxScore: number;
  color: string;
  description: string;
}

// ─── Score Line Gauge (5-segment bar) ──────────────────────────────────────
const ScoreLineGauge: React.FC<{ score: number; label?: string; thresholds?: ScoringThreshold[] }> = ({ score, label, thresholds }) => {
  const segments = 5;
  const getScoreColor = (s: number): string => {
    if (thresholds && thresholds.length > 0) {
      const t = thresholds.find(x => s >= x.minScore && s <= x.maxScore);
      if (t) return t.color;
    }
    if (s < 2) return '#dc3545';
    if (s < 3) return '#fd7e14';
    if (s < 4) return '#ffc107';
    return '#28a745';
  };
  const fill = (i: number, s: number) => (s >= i + 1 ? 100 : s <= i ? 0 : (s - i) * 100);
  const color = getScoreColor(score);
  return (
    <div className="score-line-gauge" style={{ width: '100%', padding: '8px 0' }}>
      {label && <div style={{ fontSize: 13, fontWeight: 500, color: '#333', marginBottom: 8, textAlign: 'center' }}>{label}</div>}
      <div style={{ fontSize: 24, fontWeight: 700, color, textAlign: 'center', marginBottom: 8 }}>{score > 0 ? score.toFixed(2) : 'N/A'}</div>
      <div style={{ display: 'flex', gap: 3, width: '100%', height: 16, borderRadius: 4, overflow: 'hidden' }}>
        {Array.from({ length: segments }).map((_, i) => {
          const f = fill(i, score);
          return (
            <div key={i} style={{ flex: 1, height: '100%', backgroundColor: '#e0e0e0', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
              {f > 0 && <div style={{ position: 'absolute', left: 0, top: 0, width: `${f}%`, height: '100%', backgroundColor: color, transition: 'width 0.3s ease' }} />}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#666', marginTop: 4, padding: '0 2px' }}>
        <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
      </div>
    </div>
  );
};

// ─── Dial Gauge (semicircle) ────────────────────────────────────────────────
const DialGauge: React.FC<{ score: number; maxScore?: number; size?: 'large' | 'medium' | 'small'; showRecommendation?: boolean; recommendation?: string; thresholds?: ScoringThreshold[] }> = ({ score, maxScore = 5, size = 'medium', showRecommendation, recommendation, thresholds }) => {
  const dims = { large: { width: 220, height: 140, strokeWidth: 16, fontSize: 32 }, medium: { width: 160, height: 100, strokeWidth: 12, fontSize: 24 }, small: { width: 120, height: 80, strokeWidth: 8, fontSize: 18 } };
  const { width, height, strokeWidth, fontSize } = dims[size];
  const radius = (width - strokeWidth) / 2;
  const cx = width / 2, cy = height - 10;
  const startAngle = Math.PI, endAngle = 2 * Math.PI;
  const scoreAngle = startAngle + (score / maxScore) * Math.PI;
  const getScoreColor = (s: number): string => {
    if (thresholds && thresholds.length > 0) {
      const t = thresholds.find(x => s >= x.minScore && s <= x.maxScore);
      if (t) return t.color;
    }
    if (s < 2) return '#dc3545';
    if (s < 3) return '#fd7e14';
    if (s < 4) return '#ffc107';
    return '#28a745';
  };
  const color = getScoreColor(score);
  const arc = (a1: number, a2: number, r: number) => {
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
    const large = a2 - a1 > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };
  return (
    <div className="dial-gauge" style={{ width, textAlign: 'center' }}>
      <svg width={width} height={height + 30} viewBox={`0 0 ${width} ${height + 30}`}>
        <path d={arc(startAngle, endAngle, radius)} fill="none" stroke="#e0e0e0" strokeWidth={strokeWidth} strokeLinecap="round" />
        {score > 0 && <path d={arc(startAngle, scoreAngle, radius)} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />}
        <text x={cx} y={cy - 10} textAnchor="middle" fill={color} fontSize={fontSize} fontWeight="700">{score > 0 ? score.toFixed(2) : 'N/A'}</text>
        <text x={strokeWidth / 2} y={cy + 15} textAnchor="start" fill="#999" fontSize={10}>1</text>
        <text x={width - strokeWidth / 2} y={cy + 15} textAnchor="end" fill="#999" fontSize={10}>5</text>
      </svg>
      {showRecommendation && recommendation && (
        <div style={{ backgroundColor: color, color: 'white', padding: '6px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600, marginTop: 8, display: 'inline-block' }}>{recommendation}</div>
      )}
    </div>
  );
};

interface ScoringDetailsPanelProps {
  applicationId: string;
  applicationName: string;
  applicationCode?: string;
  active?: boolean;
  embedded?: boolean;
}

/**
 * Reusable Scoring Details content (Summary + Overall gauge + expandable
 * Dimension → Component → Question breakdown). Used inline on the Disposition 360
 * "Open" view and wrappable in a modal elsewhere.
 */
const ScoringDetailsPanel: React.FC<ScoringDetailsPanelProps> = ({ applicationId, applicationName, applicationCode, active = true, embedded = false }) => {
  const [scoringData, setScoringData] = useState<ScoringData | null>(null);
  const [thresholds, setThresholds] = useState<ScoringThreshold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDimension, setExpandedDimension] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const th = await apiService.getScoringThresholds();
      setThresholds(th);
      const portfolio = await apiService.getScoringPortfolioData();
      const app = portfolio?.applications?.find((a: any) => a.application_id === applicationId);
      if (app) {
        setScoringData({
          application_id: app.application_id,
          application_name: app.application_name,
          application_id_code: app.application_id_code,
          overall_score: app.overall_score || 0,
          recommendation: app.recommendation || 'N/A',
          recommendation_color: app.recommendation_color,
          assessment_status: app.assessment_status,
          dimension_scores: app.dimension_scores || [],
        });
      } else {
        setScoringData(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load scoring data');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => { if (active) loadData(); }, [active, loadData]);

  const scoreColor = (s: number): string => {
    if (thresholds.length > 0) {
      const t = thresholds.find(x => s >= x.minScore && s <= x.maxScore);
      if (t) return t.color;
    }
    if (s < 2) return '#C00000';
    if (s < 3) return '#ED7D31';
    if (s < 4) return '#007bff';
    return '#00B500';
  };
  const scoreBg = (s: number): string => (s < 2 ? 'rgba(192,0,0,0.1)' : s < 3 ? 'rgba(237,125,49,0.1)' : s < 4 ? 'rgba(0,123,255,0.1)' : 'rgba(0,181,0,0.1)');

  const summaryCols = embedded ? 'repeat(auto-fit, minmax(88px, 1fr))' : 'repeat(5, 1fr)';
  const dimCols = embedded ? 'repeat(auto-fit, minmax(170px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))';

  if (loading) return <div className="scoring-modal__loading"><div className="scoring-modal__spinner" /><h3>Loading Scoring Data…</h3></div>;
  if (error) return <div className="scoring-modal__error"><div className="scoring-modal__error-icon">⚠️</div><h3>Error Loading Data</h3><p>{error}</p><button className="scoring-modal__retry-btn" onClick={loadData}>Retry</button></div>;
  if (!scoringData) return <div className="scoring-modal__empty"><div className="scoring-modal__empty-icon">📋</div><h3>No Scoring Data</h3><p>This application has not been fully scored yet. Complete its Q&amp;A assessment on the Application Portfolio Scoring screen to see the breakdown here.</p></div>;

  const card = (value: React.ReactNode, lbl: string) => (
    <div className="summary-card" style={{ textAlign: 'center', padding: embedded ? '14px 10px' : '20px 16px', backgroundColor: '#f9f9f9', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {value}
      <div className="summary-label" style={{ fontSize: 13, color: '#666', fontWeight: 500 }}>{lbl}</div>
    </div>
  );

  return (
    <div className="scoring-modal__data">
      <div className="scoring-section scoring-section--summary" style={{ marginBottom: 20 }}>
        <div className="scoring-section__header"><h3>Score Summary</h3></div>
        <div className="scoring-section__content">
          <div className="score-summary-grid" style={{ display: 'grid', gridTemplateColumns: summaryCols, gap: 16 }}>
            {card(<div className="summary-value" style={{ fontSize: 32, fontWeight: 700, color: scoreColor(scoringData.overall_score), marginBottom: 8 }}>{scoringData.overall_score.toFixed(2)}</div>, 'Overall Score')}
            {card(<div className="summary-value" style={{ fontSize: 20, fontWeight: 700, color: scoreColor(scoringData.overall_score), backgroundColor: scoreBg(scoringData.overall_score), padding: '6px 16px', borderRadius: 16, display: 'inline-block', marginBottom: 8 }}>{scoringData.recommendation}</div>, 'Disposition')}
            {card(<div className="summary-value" style={{ fontSize: 32, fontWeight: 700, color: '#333', marginBottom: 8 }}>{scoringData.dimension_scores.length}</div>, 'Dimensions')}
            {card(<div className="summary-value" style={{ fontSize: 32, fontWeight: 700, color: '#333', marginBottom: 8 }}>{scoringData.dimension_scores.reduce((a, d) => a + (d.component_scores?.length || 0), 0)}</div>, 'Components')}
            {card(<div className="summary-value" style={{ fontSize: 32, fontWeight: 700, color: '#333', marginBottom: 8 }}>{scoringData.dimension_scores.reduce((a, d) => a + (d.component_scores?.reduce((c: number, comp: ComponentScore) => c + (comp.question_scores?.length || comp.total_questions || 0), 0) || 0), 0)}</div>, 'Questions')}
          </div>
        </div>
      </div>

      <div className="scoring-section scoring-section--overall">
        <div className="scoring-section__header"><h3>Overall Application Score</h3></div>
        <div className="scoring-section__content">
          <div className="overall-score-container">
            <div className="overall-score-gauge"><DialGauge score={scoringData.overall_score} size={embedded ? 'medium' : 'large'} showRecommendation recommendation={scoringData.recommendation} thresholds={thresholds} /></div>
            <div className="recommendation-description">
              {(() => {
                const t = thresholds.find(x => scoringData.overall_score >= x.minScore && scoringData.overall_score <= x.maxScore);
                return t ? (<><h4 className="recommendation-description__title">Why {t.name} {applicationCode || ''} {applicationName}?</h4><p className="recommendation-description__text">{t.description}</p></>) : null;
              })()}
            </div>
            <div className="score-legend score-legend--compact">
              <h4>Score Scale</h4>
              <div className="legend-items">
                {thresholds.map(t => (
                  <div key={t.id} className={`legend-item ${scoringData.recommendation === t.name ? 'legend-item--active' : ''}`}>
                    <span className="legend-color" style={{ backgroundColor: t.color }} />
                    <span className="legend-label">{t.name}</span>
                    <span className="legend-range">{t.minScore.toFixed(1)}-{t.maxScore.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="scoring-section scoring-section--dimensions">
        <div className="scoring-section__header"><h3>Score by Dimension</h3><span className="scoring-section__count">{scoringData.dimension_scores.length} Dimensions</span></div>
        <div className="scoring-section__content">
          <div className="dimension-gauges" style={{ display: 'grid', gridTemplateColumns: dimCols, gap: 16 }}>
            {scoringData.dimension_scores.map(dimension => (
              <div key={dimension.dimension_id} className={`dimension-gauge-card ${expandedDimension === dimension.dimension_id ? 'expanded' : ''}`}
                onClick={() => setExpandedDimension(expandedDimension === dimension.dimension_id ? null : dimension.dimension_id)}
                style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, cursor: 'pointer' }}>
                <ScoreLineGauge score={dimension.dimension_score} label={dimension.dimension_name.replace(/^[A-Z]+ - /, '')} thresholds={thresholds} />
                <div style={{ textAlign: 'center', fontSize: 12, color: '#666', marginTop: 8 }}>{expandedDimension === dimension.dimension_id ? '▲ Hide Components' : '▼ Show Components'}</div>
                {expandedDimension === dimension.dimension_id && dimension.component_scores && (
                  <div className="component-details" style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
                    {dimension.component_scores.map((component: ComponentScore) => (
                      <div key={component.component_id} className="component-card" style={{ backgroundColor: '#f9f9f9', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                        <div className="component-card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                          <span style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{component.component_name.replace(/^[A-Z]+ - /, '')}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {typeof component.component_weight === 'number' && <span style={{ color: '#555', backgroundColor: '#eee', padding: '2px 8px', borderRadius: 10, fontWeight: 500, fontSize: 11 }}>Weight: {component.component_weight}</span>}
                            <span style={{ color: scoreColor(component.component_score), backgroundColor: scoreBg(component.component_score), padding: '4px 10px', borderRadius: 12, fontWeight: 600, fontSize: 13 }}>{component.component_score.toFixed(2)}</span>
                          </div>
                        </div>
                        {component.question_scores && component.question_scores.length > 0 && (
                          <div className="question-list" style={{ marginTop: 8 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 60px', gap: 8, fontSize: 12, fontWeight: 600, color: '#666', padding: '6px 0', borderBottom: '1px solid #ddd', marginBottom: 4 }}>
                              <span>Question</span><span style={{ textAlign: 'center' }}>Weight</span><span style={{ textAlign: 'center' }}>Score</span>
                            </div>
                            {component.question_scores.map((q: QuestionScore) => (
                              <div key={q.question_id} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 60px', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                                <div style={{ fontSize: 12, color: '#333', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as 'vertical' }}>{q.question_text || '-'}</div>
                                <span style={{ textAlign: 'center', fontSize: 12, color: '#666' }}>{q.weight}</span>
                                <span style={{ textAlign: 'center', color: scoreColor(q.score), backgroundColor: scoreBg(q.score), padding: '2px 8px', borderRadius: 8, fontWeight: 600, fontSize: 12 }}>{q.score}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoringDetailsPanel;
